import React, { useEffect, useMemo, useRef } from "react";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Strikethrough as StrikeIcon,
  List as BulletListIcon,
  ListOrdered as OrderedListIcon,
  Quote as QuoteIcon,
  Heading1 as Heading1Icon,
  Heading2 as Heading2Icon,
  Heading3 as Heading3Icon,
  Pilcrow as ParagraphIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Minus as HorizontalLineIcon,
} from "lucide-react";
import StarterKit from "@tiptap/starter-kit";
import {
  canonicalizeMessageBodyHtml,
  extractMessageBodyPlainText,
} from "../utilities/messageBodyHtml";
import { useTranslation } from "react-i18next";

import { EditorContent, useEditor, useEditorState } from "@tiptap/react";

type Props = {
  label?: React.ReactNode;
  labelId?: string;
  descriptionId?: string;
  invalid?: boolean;
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onChange: (html: string) => void;
};

type ToolbarButtonProps = {
  active?: boolean;
  disabled?: boolean;
  label: React.ReactNode;
  title: string;
  onClick: () => void;
};

function ToolbarButton({
  active = false,
  disabled = false,
  label,
  title,
  onClick,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className={`msg-editor__btn${active ? " msg-editor__btn--active" : ""}`}
      aria-pressed={active}
      aria-label={title}
      title={title}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function MessageBodyEditor({
  label = null,
  labelId = undefined,
  descriptionId = undefined,
  invalid = false,
  value,
  disabled = false,
  placeholder = "",
  onChange,
}: Props) {
  const normalizedValue = useMemo(() => value || "", [value]);
  const { t } = useTranslation("common");
  const lastSyncedValueRef = useRef(canonicalizeMessageBodyHtml(normalizedValue));

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        code: false,
        codeBlock: false,
        hardBreak: false,
      }),
    ],
    content: normalizedValue,
    editable: !disabled,
    editorProps: {
      attributes: {
        id: "message-body",
        class: "msg-richEditor__content",
        role: "textbox",
        "aria-multiline": "true",
        ...(labelId ? { "aria-labelledby": labelId } : {}),
        ...(descriptionId ? { "aria-describedby": descriptionId } : {}),
        ...(invalid ? { "aria-invalid": "true" } : {}),
      },
      handlePaste(view, event) {
        const text = event.clipboardData?.getData("text/plain") ?? "";
        event.preventDefault();
        if (text) {
          view.dispatch(view.state.tr.insertText(text));
        }
        return true;
      },
    },
    onUpdate({ editor: currentEditor }) {
      onChange(currentEditor.getHTML());
    },
  });

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      paragraph: editor?.isActive("paragraph"),
      h1: editor?.isActive("heading", { level: 1 }),
      h2: editor?.isActive("heading", { level: 2 }),
      h3: editor?.isActive("heading", { level: 3 }),
      bold: editor?.isActive("bold"),
      italic: editor?.isActive("italic"),
      underline: editor?.isActive("underline"),
      strike: editor?.isActive("strike"),
      bulletList: editor?.isActive("bulletList"),
      orderedList: editor?.isActive("orderedList"),
      blockquote: editor?.isActive("blockquote"),
      canUndo: editor?.can().undo(),
      canRedo: editor?.can().redo(),
    }),
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const nextValue = canonicalizeMessageBodyHtml(normalizedValue);
    const currentValue = canonicalizeMessageBodyHtml(editor.getHTML());

    if (
      nextValue === currentValue ||
      nextValue === lastSyncedValueRef.current
    ) {
      lastSyncedValueRef.current = nextValue;
      return;
    }

    lastSyncedValueRef.current = nextValue;
    editor.commands.setContent(nextValue, { emitUpdate: false });
  }, [editor, normalizedValue]);

  const isEmpty = !extractMessageBodyPlainText(normalizedValue).trim();

  return (
    <>
      <div className="msg-editor__header msg-editor__header--toolbarOnly">
        {label ? (
          <div className="msg-editor__label" id={labelId}>
            {label}
          </div>
        ) : null}

        <div className="msg-editor__toolbar">
          <ToolbarButton
            active={editorState?.paragraph}
            disabled={disabled || !editor}
            label={<ParagraphIcon />}
            title={t("richTextBar.paragraph")}
            onClick={() => editor?.chain().focus().setParagraph().run()}
          />
          <ToolbarButton
            active={editorState?.h1}
            disabled={disabled || !editor}
            label={<Heading1Icon />}
            title={t("richTextBar.heading1")}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 1 }).run()
            }
          />
          <ToolbarButton
            active={editorState?.h2}
            disabled={disabled || !editor}
            label={<Heading2Icon />}
            title={t("richTextBar.heading2")}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 2 }).run()
            }
          />
          <ToolbarButton
            active={editorState?.h3}
            disabled={disabled || !editor}
            label={<Heading3Icon />}
            title={t("richTextBar.heading3")}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 3 }).run()
            }
          />
          <ToolbarButton
            active={editorState?.bold}
            disabled={disabled || !editor}
            label={<BoldIcon />}
            title={t("richTextBar.bold")}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          />
          <ToolbarButton
            active={editorState?.italic}
            disabled={disabled || !editor}
            label={<ItalicIcon />}
            title={t("richTextBar.italic")}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          />
          <ToolbarButton
            active={editorState?.underline}
            disabled={disabled || !editor}
            label={<UnderlineIcon />}
            title={t("richTextBar.underline")}
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
          />
          <ToolbarButton
            active={editorState?.strike}
            disabled={disabled || !editor}
            label={<StrikeIcon />}
            title={t("richTextBar.strike")}
            onClick={() => editor?.chain().focus().toggleStrike().run()}
          />
          <ToolbarButton
            active={editorState?.bulletList}
            disabled={disabled || !editor}
            label={<BulletListIcon />}
            title={t("richTextBar.bulletList")}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          />
          <ToolbarButton
            active={editorState?.orderedList}
            disabled={disabled || !editor}
            label={<OrderedListIcon />}
            title={t("richTextBar.orderedList")}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          />
          <ToolbarButton
            active={editorState?.blockquote}
            disabled={disabled || !editor}
            label={<QuoteIcon />}
            title={t("richTextBar.blockquote")}
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          />
          <ToolbarButton
            disabled={disabled || !editor}
            label={<HorizontalLineIcon />}
            title={t("richTextBar.horizonLine")}
            onClick={() => editor?.chain().focus().setHorizontalRule().run()}
          />
          <ToolbarButton
            disabled={disabled || !editor || !editorState?.canUndo}
            label={<UndoIcon />}
            title={t("richTextBar.undo")}
            onClick={() => editor?.chain().focus().undo().run()}
          />
          <ToolbarButton
            disabled={disabled || !editor || !editorState?.canRedo}
            label={<RedoIcon />}
            title={t("richTextBar.redo")}
            onClick={() => editor?.chain().focus().redo().run()}
          />
        </div>
      </div>

      <div className="msg-editor__body msg-editor__body--rich">
        <div className="msg-richEditor">
          {isEmpty && placeholder ? (
            <div className="msg-richEditor__placeholder">{placeholder}</div>
          ) : null}
          <EditorContent editor={editor} />
        </div>
      </div>
    </>
  );
}

export default MessageBodyEditor;
